import {createSelector} from 'reselect';

import {Controller} from '@stubhub/react-store-provider';
import {get} from '@stubhub/rest-method';

const DEFAULT_SEARCH_CACHE_MAX_AGE = 300; // 5 min
const SEARCH_CACHE_MAX_AGE = parseInt(process.env.REACT_APP_CACHE_TTL_SEARCH, 10) || DEFAULT_SEARCH_CACHE_MAX_AGE;
const DEFAULT_SUGGEST_VARIANT = 0;
const SUGGEST_API_END_POINT = '/search/suggest/v3';

const deviceSelector = (state) => state.device;
const deviceTypeSelector = createSelector(deviceSelector, (/* istanbul ignore next */ device = {}) => device.type);
const searchSuggestionsSelector = createSelector(
  (localState) => localState.searchSuggestions,
  (searchSuggestions) => searchSuggestions || []
);
const viewportStateSelector = createSelector(
  (state) => state.viewport,
  (viewport) => viewport || {}
);

const controller = new Controller({
  namespace: 'searchjumbotron',

  mapStateToProps(state) {
    const viewportState = viewportStateSelector(state);
    const localState = this.getLocalState(state);
    const {term = ''} = localState;
    const {query = ''} = localState;
    const {response = ''} = localState;
    const {error = ''} = localState;

    const searchSuggestions = term ? searchSuggestionsSelector(localState) : null;

    return {
      screenSize: viewportState.screenSize,
      deviceType: deviceTypeSelector(state),
      loading: localState.loading,
      searchSuggestions,
      term,
      query,
      response,
      error,
      selectedSuggestionIndex: localState.selectedSuggestionIndex >= 0 ? localState.selectedSuggestionIndex : -1,
      isModalSearchActive: localState.isModalSearchActive || false,
    };
  },
  actionCreators: {
    getSearchSuggestions,
  },
  reducers: ['SEARCH_JUMBOTRON_LOADED'],
});

function sanitizeTerm(term) {
  let sanitizedTerm = term;
  if (sanitizedTerm) {
    sanitizedTerm = sanitizedTerm.trim();
  }

  return sanitizedTerm;
}

function getSuggestionsForVariant({
  suggestVariant,
  searchsuggestionImprovement,
  body = {},
  searchTerm,
  maxEventSuggestions,
}) {
  let searchSuggestions;

  switch (suggestVariant) {
    case 'SUGGEST_VARIANT_7': {
      searchSuggestions = processVariant7Response({
        body,
        searchTerm,
        searchsuggestionImprovement,
        maxEventSuggestions,
      });
      break;
    }

    default: {
      searchSuggestions = processVariant0Response(body);
      break;
    }
  }

  return searchSuggestions;
}

function getSearchQuery(opts) {
  let {term} = opts;
  const {
    sort = 'popularity desc',
    perfRows = 5,
    entityList = 'performer',
    minAvailableTickets = 0,
    includeNoneventEntities = true,
    shstore = 1,
    point,
    radius = 200,
    suggestVariant = DEFAULT_SUGGEST_VARIANT,
    lang = true,
  } = opts;

  term = sanitizeTerm(term);

  const searchSpec = {
    term,
    sort,
    perfRows,
    entityList,
    minAvailableTickets,
    includeNoneventEntities,
    shstore,
    point,
    radius,
    lang,
  };

  if (suggestVariant === 'SUGGEST_VARIANT_7') {
    searchSpec.timeZoneOffsetInMinutes = new Date().getTimezoneOffset();
    searchSpec.entityList = 'event,performer,venue';
    searchSpec.withEventCount = true;

    searchSpec.enablePopular = false;
    searchSpec.sort = 'popularity desc, eventDateLocal asc';
  }

  return searchSpec;
}

function getSearchSuggestions(opts = {}) {
  const {suggestVariant, searchsuggestionImprovement, maxEventSuggestions} = opts;

  return (dispatch, getState) => {
    const {gsConfig, lang} = getState() || {};
    if (gsConfig) {
      opts.shstore = gsConfig.shstoreId;
    }

    const searchQuery = getSearchQuery(opts, searchsuggestionImprovement);
    dispatch({
      type: 'SEARCH_JUMBOTRON_LOADING',
    });

    return get({
      host: process.env.REACT_APP_API_HOST,
      path: SUGGEST_API_END_POINT,
      query: searchQuery,
      headers: {
        'Accept-Language': lang,
        Authorization: process.env.REACT_APP_API_SECRET,
      },
      json: true,
      cache: true,
      cacheMaxAge: SEARCH_CACHE_MAX_AGE,
    })
      .then((body) => {
        const {searchTerm} = body;

        const searchSuggestions = getSuggestionsForVariant({
          body,
          suggestVariant,
          searchsuggestionImprovement,
          searchTerm,
          maxEventSuggestions,
        });
        dispatch({
          type: 'SEARCH_JUMBOTRON_LOADED',
          searchSuggestions,
          term: searchTerm,
          query: searchQuery,
          response: body,
          error: '',
          loading: false,
          selectedSuggestionIndex: -1,
        });

        return null;
      })
      .catch((error) => {
        dispatch({
          type: 'SEARCH_JUMBOTRON_LOADED',
          error,
          query: searchQuery,
        });
      });
  };
}

function getPerformerSuggestionItem({performerSuggestionObject = {}}) {
  const performerSuggestionItem = {...performerSuggestionObject};

  return performerSuggestionItem;
}

function processVariant0Response(body) {
  const {performers = []} = body;
  const searchSuggestionObject = [];

  for (const performer of performers) {
    searchSuggestionObject.push(
      getPerformerSuggestionItem({
        performerSuggestionObject: performer,
      })
    );
  }

  return searchSuggestionObject;
}

function processImprovedPerformerSuggestions({performers = {}, maxPerformerSuggestions = 3, count = 1}) {
  const searchSuggestions = [];

  if (performers && performers.length > 0) {
    for (let i = 0, performerArrLen = Math.min(maxPerformerSuggestions, performers.length); i < performerArrLen; i++) {
      const performerItem = getPerformerSuggestionItem({
        performerSuggestionObject: performers[i],
      });
      searchSuggestions.push(performerItem);
      count++;
    }
  }

  return {
    searchSuggestions,
    count,
  };
}

function processVenueSuggestions({venues = {}, maxVenueSuggestions = 2, count = 0}) {
  const searchSuggestions = [];

  if (venues && venues.length > 0) {
    for (let i = 0, venuesArrLen = Math.min(maxVenueSuggestions, venues.length); i < venuesArrLen; i++) {
      const venueItem = {...venues[i]};

      searchSuggestions.push({...venueItem});
      count++;
    }
  }

  return {
    searchSuggestions,
    count,
  };
}

function processVariant7Response({body, searchTerm}) {
  let searchSuggestionObject = [];
  let otherSuggestionObject = [];
  let venueSuggestionObject = [];

  const {queryLogSuggestions, events, venues, performers} = body;
  let suggestedEventCount;
  let suggestedPerformercount;

  if (queryLogSuggestions || events || performers) {
    const performerSuggestionsResponse = processImprovedPerformerSuggestions({
      performers,
    });
    suggestedPerformercount = performerSuggestionsResponse.searchSuggestions
      ? performerSuggestionsResponse.searchSuggestions.length
      : 0;

    otherSuggestionObject = [...performerSuggestionsResponse.searchSuggestions];
  }
  if (venues) {
    const perfEventCount = suggestedPerformercount + suggestedEventCount;

    const venueSuggestionsResponse = processVenueSuggestions({
      venues,
      searchTerm,
      perfEventCount,
    });
    venueSuggestionObject = [...venueSuggestionsResponse.searchSuggestions];
  }

  searchSuggestionObject = [...otherSuggestionObject, ...venueSuggestionObject];

  return searchSuggestionObject;
}

export default controller;
